/**
 * B2B Quick Order — full catalog (collections/all), category tabs, search, bulk cart add.
 */
(function () {
  if (customElements.get('em-b2b-quickorder')) return;

  class EmB2bQuickorder extends HTMLElement {
    connectedCallback() {
      document.body.classList.add('em-b2b-quickorder-page');

      this.root = this.normalizeRoot(this.getAttribute('data-root') || '/');
      this.activeHandle = 'all';
      this.searchQuery = '';
      this.currentPage = 1;
      this.totalPages = 1;
      this.loading = false;
      this.searchTimer = null;

      this.list = this.querySelector('[data-em-b2b-list]');
      this.searchInput = this.querySelector('[data-em-b2b-search]');
      this.tabs = this.querySelectorAll('[data-em-b2b-tab]');
      this.emptyEl = this.querySelector('[data-em-b2b-empty]');
      this.summaryEl = this.querySelector('[data-em-b2b-summary]');
      this.errorEl = this.querySelector('[data-em-b2b-error]');
      this.addAllBtn = this.querySelector('[data-em-b2b-add-all]');
      this.loadWrap = this.querySelector('[data-em-b2b-load-wrap]');
      this.loadBtn = this.querySelector('[data-em-b2b-load]');

      this.readMetaFrom(this.list);
      this.bindExistingRows();
      this.updateLoadMore();
      this.updateSummary();

      this.searchInput?.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          this.searchQuery = (this.searchInput.value || '').trim();
          this.reloadFirstPage();
        }, 280);
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const handle = tab.getAttribute('data-handle') || 'all';
          if (handle === this.activeHandle && !this.searchQuery) return;

          this.activeHandle = handle;
          this.tabs.forEach((t) => {
            const on = t === tab;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-pressed', on ? 'true' : 'false');
          });

          if (this.searchInput && this.searchQuery) {
            this.searchInput.value = '';
            this.searchQuery = '';
          }

          this.reloadFirstPage();
        });
      });

      this.loadBtn?.addEventListener('click', () => this.loadMore());

      this.addAllBtn?.addEventListener('click', () => {
        const items = this.collectItems();
        this.addItems(items, this.addAllBtn).then((ok) => {
          if (!ok) return;
          this.querySelectorAll('[data-em-b2b-qty]').forEach((input) => {
            input.value = '0';
          });
          this.updateSummary();
        });
      });
    }

    normalizeRoot(root) {
      if (!root || root === '/') return '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    get rows() {
      return Array.from(this.querySelectorAll('[data-em-b2b-row]'));
    }

    bindExistingRows() {
      this.rows.forEach((row) => this.bindRow(row));
    }

    bindRow(row) {
      if (row.dataset.emBound === '1') return;
      row.dataset.emBound = '1';

      const qtyInput = row.querySelector('[data-em-b2b-qty]');
      const variantEl = row.querySelector('[data-em-b2b-variant]');

      qtyInput?.addEventListener('change', () => this.updateSummary());
      qtyInput?.addEventListener('input', () => this.updateSummary());

      if (variantEl?.tagName === 'SELECT') {
        variantEl.addEventListener('change', () => {
          const selected = variantEl.options[variantEl.selectedIndex];
          const price = selected?.getAttribute('data-price') || '';
          const sku = selected?.getAttribute('data-sku') || '';
          const priceNode = row.querySelector('[data-em-b2b-price]');
          if (priceNode) priceNode.textContent = price;
          const skuNode = row.querySelector('[data-em-b2b-sku]');
          if (skuNode) {
            skuNode.textContent = sku ? `SKU: ${sku}` : '';
            skuNode.hidden = !sku;
          }
        });
      }
    }

    readMetaFrom(scope) {
      const meta = scope?.querySelector('[data-em-b2b-meta]');
      if (!meta) {
        this.currentPage = 1;
        this.totalPages = 1;
        return;
      }
      this.currentPage = parseInt(meta.getAttribute('data-page') || '1', 10) || 1;
      this.totalPages = parseInt(meta.getAttribute('data-pages') || '1', 10) || 1;
      meta.remove();
    }

    updateLoadMore() {
      const show = this.currentPage < this.totalPages;
      if (this.loadWrap) this.loadWrap.hidden = !show;
    }

    updateEmpty() {
      if (this.emptyEl) this.emptyEl.hidden = this.rows.length > 0;
    }

    clearRows() {
      this.querySelectorAll('[data-em-b2b-row]').forEach((row) => row.remove());
    }

    buildFetchUrl(page) {
      const origin = window.location.origin;
      const root = this.root === '/' ? '' : this.root.replace(/\/$/, '');

      if (this.searchQuery) {
        const url = new URL(`${root}/search`, origin);
        url.searchParams.set('q', this.searchQuery);
        url.searchParams.set('type', 'product');
        url.searchParams.set('view', 'em-b2b-rows');
        url.searchParams.set('page', String(page));
        return url.toString();
      }

      const handle = this.activeHandle === 'all' ? 'all' : this.activeHandle;
      const url = new URL(`${root}/collections/${encodeURIComponent(handle)}`, origin);
      url.searchParams.set('view', 'em-b2b-rows');
      url.searchParams.set('page', String(page));
      return url.toString();
    }

    async fetchPage(page) {
      const response = await fetch(this.buildFetchUrl(page));
      if (!response.ok) throw new Error('fetch failed');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const rows = Array.from(doc.querySelectorAll('[data-em-b2b-row]'));
      const meta = doc.querySelector('[data-em-b2b-meta]');
      return { rows, meta };
    }

    async reloadFirstPage() {
      if (this.loading) return;
      this.loading = true;
      this.setError('');
      this.loadBtn?.setAttribute('aria-busy', 'true');

      try {
        const { rows, meta } = await this.fetchPage(1);
        this.clearRows();
        rows.forEach((row) => {
          const imported = document.importNode(row, true);
          this.list.appendChild(imported);
          this.bindRow(imported);
        });
        if (meta) {
          this.currentPage = parseInt(meta.getAttribute('data-page') || '1', 10) || 1;
          this.totalPages = parseInt(meta.getAttribute('data-pages') || '1', 10) || 1;
        } else {
          this.currentPage = 1;
          this.totalPages = rows.length ? 1 : 1;
        }
        this.updateLoadMore();
        this.updateEmpty();
        this.updateSummary();
      } catch (error) {
        this.setError('Produkte konnten nicht geladen werden. Bitte erneut versuchen.');
      } finally {
        this.loading = false;
        this.loadBtn?.removeAttribute('aria-busy');
      }
    }

    async loadMore() {
      if (this.loading || this.currentPage >= this.totalPages) return;
      this.loading = true;
      this.loadBtn?.setAttribute('aria-busy', 'true');

      try {
        const next = this.currentPage + 1;
        const { rows, meta } = await this.fetchPage(next);
        rows.forEach((row) => {
          const imported = document.importNode(row, true);
          this.list.appendChild(imported);
          this.bindRow(imported);
        });
        if (meta) {
          this.currentPage = parseInt(meta.getAttribute('data-page') || String(next), 10) || next;
          this.totalPages = parseInt(meta.getAttribute('data-pages') || String(this.totalPages), 10) || this.totalPages;
        } else {
          this.currentPage = next;
        }
        this.updateLoadMore();
        this.updateEmpty();
        this.updateSummary();
      } catch (error) {
        this.setError('Weitere Produkte konnten nicht geladen werden.');
      } finally {
        this.loading = false;
        this.loadBtn?.removeAttribute('aria-busy');
      }
    }

    getVariantId(row) {
      const el = row.querySelector('[data-em-b2b-variant]');
      return el ? parseInt(el.value, 10) : NaN;
    }

    collectItems() {
      const items = [];
      this.rows.forEach((row) => {
        const qtyInput = row.querySelector('[data-em-b2b-qty]');
        const qty = Math.max(0, parseInt(qtyInput?.value || '0', 10) || 0);
        const id = this.getVariantId(row);
        if (qty > 0 && id) items.push({ id, quantity: qty });
      });
      return items;
    }

    updateSummary() {
      const items = this.collectItems();
      const positions = items.length;
      const units = items.reduce((sum, item) => sum + item.quantity, 0);
      if (this.summaryEl) {
        this.summaryEl.textContent =
          positions === 0
            ? '0 Positionen ausgewählt'
            : `${positions} Position${positions === 1 ? '' : 'en'} · ${units} Stück`;
      }
      if (this.addAllBtn) this.addAllBtn.disabled = positions === 0;
      this.classList.toggle('has-selection', positions > 0);
    }

    setError(message) {
      if (!this.errorEl) return;
      if (!message) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
        return;
      }
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    }

    async addItems(items, button) {
      this.setError('');
      if (!items.length) return false;

      const routes = window.theme?.routes || {};
      const cartAddUrl = routes.cart_add_url || '/cart/add.js';
      const cartUrl = routes.cart_url || '/cart.js';
      const fetchConfig = window.theme?.utils?.fetchConfig
        ? window.theme.utils.fetchConfig('javascript')
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          };

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      button?.setAttribute('aria-busy', 'true');
      button?.setAttribute('aria-disabled', 'true');

      try {
        const response = await fetch(cartAddUrl, {
          ...fetchConfig,
          body: JSON.stringify({
            items,
            sections: sectionsToBundle,
            sections_url: window.location.pathname,
          }),
        });
        const parsed = await response.json();

        if (parsed.status) {
          this.setError(parsed.description || parsed.message || 'Artikel konnte nicht hinzugefügt werden.');
          return false;
        }

        const cartRes = await fetch(
          cartUrl,
          window.theme?.utils?.fetchConfig?.('json', 'GET') || {
            method: 'GET',
            headers: { Accept: 'application/json' },
          }
        );
        const cartJson = await cartRes.json();
        cartJson.sections = parsed.sections;

        if (window.theme?.pubsub?.publish && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'em-b2b-quickorder',
            cart: cartJson,
          });
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsed } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
        return true;
      } catch (error) {
        this.setError('Netzwerkfehler beim Hinzufügen. Bitte erneut versuchen.');
        return false;
      } finally {
        button?.removeAttribute('aria-busy');
        button?.removeAttribute('aria-disabled');
        this.updateSummary();
      }
    }
  }

  customElements.define('em-b2b-quickorder', EmB2bQuickorder);
})();
